import React from 'react';

import { defineCatalogElement } from '@archef2000/react-planner';
import * as Three from 'three';
import { Group, Object3DEventMap } from 'three';

import { loadObjWithMaterial } from '../../utils/load-obj';

let cached3DWindow: Group<Object3DEventMap>;

export default defineCatalogElement({
  name: 'venetian-blind-window',
  prototype: 'holes',

  info: {
    tag: ['window'],
    title: 'Venetian Blind Window',
    description: 'Venetian Blind Window',
    image: require('./venetian.jpg')
  },

  properties: {
    width: {
      label: 'Width',
      type: 'length-measure',
      defaultValue: {
        length: 90
      }
    },
    height: {
      label: 'Height',
      type: 'length-measure',
      defaultValue: {
        length: 100
      }
    },
    altitude: {
      label: 'Altitude',
      type: 'length-measure',
      defaultValue: {
        length: 90
      }
    },
    thickness: {
      label: 'Thickness',
      type: 'length-measure',
      defaultValue: {
        length: 10
      }
    },
    flip: {
      label: 'Flip',
      type: 'checkbox',
      defaultValue: false
    }
  },

  render2D: function (element, layer, scene) {
    const STYLE_HOLE_BASE = {
      stroke: '#000',
      strokeWidth: '3px',
      fill: '#000'
    };
    const STYLE_HOLE_SELECTED = {
      stroke: '#0096fd',
      strokeWidth: '3px',
      fill: '#0096fd',
      cursor: 'move'
    };

    const epsilon = 3;

    const holeWidth = element.properties.width.length;
    const holePath = `M${0} ${-epsilon}  L${holeWidth} ${-epsilon}  L${holeWidth} ${epsilon}  L${0} ${epsilon}  z`;
    const holeStyle = element.selected ? STYLE_HOLE_SELECTED : STYLE_HOLE_BASE;
    const length = element.properties.width.length;
    return (
      <g transform={`translate(${-length / 2}, 0)`}>
        <path key="1" d={holePath} style={holeStyle} />
        <line
          key="2"
          x1={holeWidth / 2}
          y1={-10 - epsilon}
          x2={holeWidth / 2}
          y2={10 + epsilon}
          style={holeStyle}
        />
      </g>
    );
  },

  render3D: async function (element, layer, scene) {
    const thickness = element.properties.thickness.length;
    const flip = element.properties.flip;

    const onLoadItem = (object: Three.Group<Three.Object3DEventMap>) => {
      const venetian = new Three.Object3D();

      const boundingBox = new Three.Box3().setFromObject(object);

      const initialWidth = boundingBox.max.x - boundingBox.min.x;
      const initialHeight = boundingBox.max.y - boundingBox.min.y;
      const initialThickness = boundingBox.max.z - boundingBox.min.z;

      if (element.selected) {
        const box = new Three.BoxHelper(object, 0x99c3fb);
        box.material.linewidth = 2;
        box.material.depthTest = false;
        box.renderOrder = 1000;
        object.add(box);
      }

      const width = element.properties.width.length;
      const height = element.properties.height.length;
      const thickness = element.properties.thickness.length;

      object.scale.set(
        width / initialWidth,
        height / initialHeight,
        thickness / 2 / initialThickness
      );

      venetian.add(object);
      venetian.add(createTenda());

      if (flip === true) venetian.rotation.y += Math.PI;

      return venetian;
    };

    if (cached3DWindow) {
      return onLoadItem(cached3DWindow.clone());
    }

    const mtl = require('./venetian.mtl');
    const obj = require('./venetian.obj');
    const img = require('./texture.png');
    const resourcePath = img.substr(0, img.lastIndexOf('/')) + '/';

    const object_4 = await loadObjWithMaterial(mtl, obj, resourcePath);
    cached3DWindow = object_4;
    return onLoadItem(cached3DWindow.clone());

    function createTenda() {
      const venetian = new Three.Object3D();

      //colors
      const white = new Three.MeshLambertMaterial({
        color: 0xffffff,
        opacity: 0.5,
        transparent: true
      });
      const grey = new Three.MeshLambertMaterial({ color: 0xcccccc });

      const roundedRectShape = new Three.Shape();

      const x = 0;
      const y = 0;
      const width = 1;
      const height = 18;
      const radius = 0.25;

      roundedRectShape.moveTo(x, y + radius);
      roundedRectShape.lineTo(x, y + height - radius);
      roundedRectShape.quadraticCurveTo(x, y + height, x + radius, y + height);
      roundedRectShape.lineTo(x + width - radius, y + height);
      roundedRectShape.quadraticCurveTo(
        x + width,
        y + height,
        x + width,
        y + height - radius
      );
      roundedRectShape.lineTo(x + width, y + radius);
      roundedRectShape.quadraticCurveTo(x + width, y, x + width - radius, y);
      roundedRectShape.lineTo(x + radius, y);
      roundedRectShape.quadraticCurveTo(x, y, x, y + radius);

      const holePath1 = new Three.Path();
      holePath1.moveTo(0.5, 0.6);
      holePath1.arc(0, 0.7, 0.15, 0, Math.PI, false);
      holePath1.arc(0.15, -0.09, 0.15, Math.PI, 0, false);
      roundedRectShape.holes.push(holePath1);

      const holePath2 = new Three.Path();
      holePath2.moveTo(0.5, 4.6);
      holePath2.arc(0, 0.7, 0.15, 0, Math.PI, false);
      holePath2.arc(0.15, -0.09, 0.15, Math.PI, 0, false);
      roundedRectShape.holes.push(holePath2);

      const holePath3 = new Three.Path();
      holePath3.moveTo(0.5, 8.6);
      holePath3.arc(0, 0.7, 0.15, 0, Math.PI, false);
      holePath3.arc(0.15, -0.09, 0.15, Math.PI, 0, false);
      roundedRectShape.holes.push(holePath3);

      const holePath4 = new Three.Path();
      holePath4.moveTo(0.5, 12.6);
      holePath4.arc(0, 0.7, 0.15, 0, Math.PI, false);
      holePath4.arc(0.15, -0.09, 0.15, Math.PI, 0, false);
      roundedRectShape.holes.push(holePath4);

      const holePath5 = new Three.Path();
      holePath5.moveTo(0.5, 16.6);
      holePath5.arc(0, 0.7, 0.15, 0, Math.PI, false);
      holePath5.arc(0.15, -0.09, 0.15, Math.PI, 0, false);
      roundedRectShape.holes.push(holePath5);

      const extrudeSettings = {
        steps: 1,
        depth: 0.2,
        bevelEnabled: false,
        bevelThickness: 0.4,
        bevelSize: 0.4,
        bevelSegments: 1
      };

      for (let i = 0; i < 25; i += 0.7) {
        const geometry = new Three.ExtrudeGeometry(
          roundedRectShape,
          extrudeSettings
        );
        const mesh = new Three.Mesh(geometry, grey);
        mesh.position.set(0, i, 0.86);
        mesh.rotation.z += Math.PI / 2;
        mesh.rotation.x += -Math.PI / 2;
        venetian.add(mesh);
      }

      for (let j = -1.25; j > -19; j += -4) {
        const geometry1 = new Three.CylinderGeometry(0.105, 0.105, 26, 32);
        const tubo = new Three.Mesh(geometry1, white);
        tubo.position.set(j, 12.5, 0.35);
        venetian.add(tubo);
      }

      const roundedRectShape2 = new Three.Shape();

      const x1 = 0;
      const y1 = 0;
      const width1 = 1;
      const height1 = 18;
      const radius1 = 0.25;

      roundedRectShape2.moveTo(x1, y1 + radius1);
      roundedRectShape2.lineTo(x1, y1 + height1 - radius1);
      roundedRectShape2.quadraticCurveTo(
        x1,
        y1 + height1,
        x1 + radius1,
        y1 + height1
      );
      roundedRectShape2.lineTo(x1 + width1 - radius1, y1 + height1);
      roundedRectShape2.quadraticCurveTo(
        x1 + width1,
        y1 + height1,
        x1 + width1,
        y1 + height1 - radius1
      );
      roundedRectShape2.lineTo(x1 + width1, y1 + radius1);
      roundedRectShape2.quadraticCurveTo(
        x1 + width1,
        y1,
        x1 + width1 - radius1,
        y1
      );
      roundedRectShape2.lineTo(x1 + radius1, y1);
      roundedRectShape2.quadraticCurveTo(x1, y1, x1, y1 + radius1);

      const extrudeSettings2 = {
        steps: 1,
        depth: 0.4,
        bevelEnabled: false,
        bevelThickness: 0.4,
        bevelSize: 0.4,
        bevelSegments: 1
      };

      for (let k = -0.5; k < 27; k += 26) {
        const geometry2 = new Three.ExtrudeGeometry(
          roundedRectShape2,
          extrudeSettings2
        );
        const mesh2 = new Three.Mesh(geometry2, grey);
        mesh2.position.set(0, k, 1);
        mesh2.rotation.z += Math.PI / 2;
        mesh2.rotation.x += -Math.PI / 2;
        venetian.add(mesh2);
      }

      const valueObject = new Three.Box3().setFromObject(venetian);

      const deltaX = Math.abs(valueObject.max.x - valueObject.min.x);
      const deltaY = Math.abs(valueObject.max.y - valueObject.min.y);
      const deltaZ = Math.abs(valueObject.max.z - valueObject.min.z);

      venetian.position.x += width1 / 0.025;
      venetian.position.y += -height1 / 0.4;
      venetian.scale.set(
        (5.2 * width1) / deltaZ,
        (5.45 * height1) / deltaY,
        (2.5 * thickness) / deltaX
      );

      return venetian;
    }
  }
});
